import React from 'react';
import { arrayOf, string, object, func, bool, oneOf } from 'prop-types';
import classNames from 'classnames';
import { noop, find } from 'lodash';
import { connect } from 'react-redux';
import { Select } from '@fiverr-private/fit';
import { selectCheckoutPlan } from '../../../actions/checkoutPlan';
import { isRecurringOrderSelected } from '../../../reducers/checkoutPlan';
import {
    getRecurringOptions,
    getSelectedRecurringOption
} from '../../../reducers/packages';
import { CHECKOUT_PLANS } from '../../../utils/checkoutPlan/constants';
import { RECURRING_NONE_OPTION } from '../../../utils/recurring/constants';
import { MP_CHANGED_CHECKOUT_PLAN, MP_CHANGED_RECURRING_OPTION } from '../../../utils/mixPanelEvents';
import { PACKAGE_POSITIONS } from '../../../types/packagePositions';
import { selectRecurringOption } from '../../../actions/packages';
import { isNoneRecurringOption } from '../../../utils/recurring';
import { translateHowLongAgo } from '../../../utils/recurring/translation';

import './style.scss';

const RecurringOptionSelect = ({
    className,
    recurringOptions,
    selectedRecurringOption,
    isRecurringOrderSelected,
    selectRecurringOption = noop,
    selectCheckoutPlan = noop,
    noneOptionAllowed = false,
    source
}, { biEvents }) => {

    const onChange = (ev) => {
        ev.preventDefault();
        const selectedRecurringOptionId = ev.target.value;
        const selectedCheckoutPlan = isNoneRecurringOption(selectedRecurringOptionId)
            ? CHECKOUT_PLANS.SINGLE_ORDER
            : CHECKOUT_PLANS.RECURRING_ORDER;

        if (selectedCheckoutPlan === CHECKOUT_PLANS.RECURRING_ORDER) {
            reportChangeRecurringOption(selectedRecurringOptionId);
        }

        reportChangeCheckoutPlan(selectedCheckoutPlan);

        selectRecurringOption({ selectedRecurringOptionId });
        selectCheckoutPlan({ selectedCheckoutPlan });
    };

    const reportChangeRecurringOption = (selectedRecurringOptionId) => {
        const { duration } = find(recurringOptions, {
            id: selectedRecurringOptionId
        });

        const params = { source, duration };

        biEvents.sendMixPanelEvent({
            eventName: MP_CHANGED_RECURRING_OPTION,
            params
        });
    };

    const reportChangeCheckoutPlan = (selectedCheckoutPlan) => {
        const params = { source, selectedCheckoutPlan };

        biEvents.sendMixPanelEvent({
            eventName: MP_CHANGED_CHECKOUT_PLAN,
            params
        });
    };

    const calculateValue = () => {
        if (!noneOptionAllowed) {return selectedRecurringOption.id;}

        return isRecurringOrderSelected
            ? selectedRecurringOption.id
            : RECURRING_NONE_OPTION.id;
    };

    const options = noneOptionAllowed
        ? [RECURRING_NONE_OPTION, ...recurringOptions]
        : recurringOptions;

    const wrapperClass = classNames(
        'recurring-option-select',
        className
    );

    return (
        <Select className={wrapperClass}
            value={calculateValue()}
            onChange={onChange}
            appendToBody={false}
            contentClassName="option-select-content">
            {options.map(({ id, duration, durationTimeUnit }) => (
                <Select.Option key={id} value={id}>
                    {translateHowLongAgo(duration, durationTimeUnit)}
                </Select.Option>
            ))}
        </Select>
    );
};

RecurringOptionSelect.propTypes = {
    className: string,
    recurringOptions: arrayOf(object),
    selectedRecurringOption: object,
    selectRecurringOption: func,
    noneOptionAllowed: bool,
    source: oneOf(Object.values(PACKAGE_POSITIONS))
};

RecurringOptionSelect.contextTypes = {
    biEvents: object
};

const mapStateToProps = (state) => {
    const { packages } = state;
    const recurringOptions = getRecurringOptions(packages);
    const selectedRecurringOption = getSelectedRecurringOption(packages);

    return {
        recurringOptions,
        isRecurringOrderSelected: isRecurringOrderSelected(state),
        selectedRecurringOption
    };
};

const mapDispatchToProps = { selectRecurringOption, selectCheckoutPlan };

export { RecurringOptionSelect };
export default connect(mapStateToProps, mapDispatchToProps)(RecurringOptionSelect);
